import React, { useEffect, useRef, useState } from "react";
import "./Selector.css";
import { BiSelectMultiple } from "react-icons/bi";
import { MdClear } from "react-icons/md";
import { capitalize } from "../../utils";
import { IoCheckmarkOutline, IoChevronForward } from "react-icons/io5";
import { themeColours } from "../../resources";

type MultiSelectorProps = {
  placeholder: string;
  listItems: string[];
  selectedValues: { [key: string]: boolean };
  handleSelectionChange: (update: string) => void;
  handleSelectionClear: () => void;
  handleSelectAll: () => void;
};

export function MultiSelector({
  placeholder,
  listItems,
  handleSelectionChange,
  handleSelectionClear,
  handleSelectAll,
  selectedValues,
}: MultiSelectorProps) {
  const [isFocus, setIsFocus] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredItems, setFilteredItems] = useState<string[]>(listItems);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter items based on input value
  useEffect(() => {
    if (inputValue) {
      setFilteredItems(listItems.filter((item) => item.toLowerCase().includes(inputValue.toLowerCase())));
    } else {
      setFilteredItems(listItems);
    }
  }, [inputValue, listItems]);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
      setIsInputVisible(false);
      setIsFocus(false);
    }
  };

  useEffect(() => {
    if (isFocus) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocus]);

  const handleClick = (item: string) => {
    handleSelectionChange(item);
    // setInputValue(''); // Clear input after selection
  };

  const handleIconClick = () => {
    if(!listItems.length) return
    if (isFocus) {
      if (selectedValues["blank"] === true) {
        return;
      }
      if (selectedItems < 1) {
        handleSelectAll();
      } else {
        handleClear();
      }
    } else {
      if(selectedItems>0) handleClear()
      else handleDropdownToggle()
    }
  };
  const handleClear = () => {
    handleSelectionClear();
    setInputValue("");
  };

  const handleDropdownToggle = () => {
    if (!isDropdownOpen) {
      // First click opens dropdown but doesn't show real input
      setIsDropdownOpen(true);
      setIsInputVisible(true);
      setIsFocus(true);
    } else {
      // Second click on the display area
      setIsInputVisible(true);
      // Focus after a slight delay to ensure the element is visible
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  let value = inputValue;
  const selectedItems = Object.keys(selectedValues).length;
  if (!isFocus) {
    value = Object.keys(selectedValues)
      .map((item) => capitalize(item))
      .join(" | ")
      .replace(/\b(Blank|Exclude|Unassigned)\b/g, "N/A");
    if (value.length > 30) {
      value = `${selectedItems} selected`;
    }
  }

  const sortedItems = filteredItems.sort();
  let icon = <></>;
  if (!isFocus) {
    if (selectedItems > 0) {
      icon = <MdClear />;
    } else {
      icon = <IoChevronForward color={themeColours.tertiary} />;
    }
    if(!listItems.length) icon = <></>
  }
  if (isFocus) {
    icon = selectedItems < 1 ? <BiSelectMultiple /> : <MdClear />;
    if(!listItems.length) icon = <></>
  }
  return (
    <div className="selectorContainer" ref={dropdownRef}>
      <span className={isFocus ? "inputClearSpanFocus" : "inputClearSpan"}>
        {!isInputVisible && (
          <div className={`input ${value ? "fakeInputValue" : "fakeInputPlaceholder"}`} onClick={handleDropdownToggle}>
            {value || placeholder}
          </div>
        )}
        {isInputVisible && (
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onClick={() => setIsFocus(true)}
            className={isFocus ? "inputFocus" : "input"}
          />
        )}
        <div className={isFocus ? "inputClearFocus" : "inputClear"} onClick={handleIconClick}>
          {icon}
        </div>
      </span>
      {isDropdownOpen && (
        <div className="menuList">
          {filteredItems.length > 0 ? (
            sortedItems.map((item) => (
              <div
                key={item}
                onClick={() => handleClick(item)}
                className={selectedValues[item] ? "menuItemSelected" : "menuItem"}
              >
                {capitalize(item)}
                {selectedValues[item] && <IoCheckmarkOutline color={themeColours.primary} />}
              </div>
            ))
          ) : (
            <div className="menuItem">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
}
